import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { 
  CheckCircle, 
  Clock, 
  ChefHat, 
  Truck, 
  X, 
  Bell,
  Package
} from 'lucide-react';

interface OrderStatusNotificationProps {
  isVisible: boolean;
  onClose: () => void;
  orderId?: string;
  status: 'confirmed' | 'preparing' | 'cooking' | 'ready' | 'out-for-delivery' | 'delivered';
  estimatedTime?: string;
}

export function OrderStatusNotification({ 
  isVisible, 
  onClose, 
  orderId = 'ET123456',
  status,
  estimatedTime = '25-35 min'
}: OrderStatusNotificationProps) {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
    }
  }, [isVisible]);

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'confirmed':
        return {
          icon: CheckCircle,
          title: 'Order Confirmed!',
          message: 'We received your order and are preparing it.',
          color: 'bg-emerald-500',
          bgColor: 'bg-emerald-50',
          textColor: 'text-emerald-800',
          borderColor: 'border-emerald-200'
        };
      case 'preparing':
        return {
          icon: Package,
          title: 'Order Being Prepared',
          message: 'Our chef is preparing your delicious meal.',
          color: 'bg-blue-500',
          bgColor: 'bg-blue-50',
          textColor: 'text-blue-800',
          borderColor: 'border-blue-200'
        };
      case 'cooking':
        return {
          icon: ChefHat,
          title: 'Cooking in Progress',
          message: 'Your food is being cooked to perfection.',
          color: 'bg-orange-500',
          bgColor: 'bg-orange-50',
          textColor: 'text-orange-800',
          borderColor: 'border-orange-200'
        };
      case 'ready':
        return {
          icon: Bell,
          title: 'Order Ready!',
          message: 'Your order is ready for pickup.',
          color: 'bg-green-500',
          bgColor: 'bg-green-50',
          textColor: 'text-green-800',
          borderColor: 'border-green-200'
        };
      case 'out-for-delivery':
        return {
          icon: Truck,
          title: 'Out for Delivery',
          message: 'Your order is on its way to you.',
          color: 'bg-purple-500',
          bgColor: 'bg-purple-50',
          textColor: 'text-purple-800',
          borderColor: 'border-purple-200'
        };
      case 'delivered':
        return {
          icon: CheckCircle,
          title: 'Order Delivered!',
          message: 'Your order has been delivered. Enjoy!',
          color: 'bg-emerald-500',
          bgColor: 'bg-emerald-50',
          textColor: 'text-emerald-800',
          borderColor: 'border-emerald-200'
        };
      default:
        return {
          icon: Clock,
          title: 'Processing Order',
          message: 'We are processing your order.',
          color: 'bg-gray-500',
          bgColor: 'bg-gray-50',
          textColor: 'text-gray-800',
          borderColor: 'border-gray-200'
        };
    }
  };

  const statusInfo = getStatusInfo(status);
  const StatusIcon = statusInfo.icon;

  if (!isVisible) return null;

  return (
    <div className="fixed top-4 right-4 z-50 animate-slide-in-right">
      <Card className={`w-80 shadow-xl border-2 ${statusInfo.borderColor} ${statusInfo.bgColor} ${isAnimating ? 'animate-bounce-gentle' : ''}`}>
        <CardContent className="p-4">
          <div className="flex items-start space-x-3">
            <div className={`w-10 h-10 ${statusInfo.color} rounded-full flex items-center justify-center animate-pulse-slow`}>
              <StatusIcon className="w-5 h-5 text-white" />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between mb-1">
                <h4 className={`font-bold text-sm ${statusInfo.textColor}`}>
                  {statusInfo.title}
                </h4>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  className="h-6 w-6 p-0 text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
              
              <p className={`text-xs ${statusInfo.textColor} mb-2`}>
                {statusInfo.message}
              </p>
              
              <div className="flex items-center justify-between">
                <Badge variant="secondary" className="text-xs">
                  Order #{orderId}
                </Badge>
                {estimatedTime && status !== 'delivered' && (
                  <div className="flex items-center space-x-1 text-xs text-gray-600">
                    <Clock className="w-3 h-3" />
                    <span>{estimatedTime}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}